import React from "react";
import { Modal } from "react-bootstrap";
import styled, { keyframes } from "styled-components";

/**
 * Error modal — shown when the atomic submit fails.
 *
 * Important UX rule:
 *  Never show backend internals, stack traces, or DB column names.
 *  Map the normalized error code to a user-friendly message in the lookup
 *  below. Anything not in the map gets a generic fallback.
 *
 * Note on retry safety:
 *  Because the backend uses authorize → DB → capture, a failure at any
 *  step means the card was NOT charged. The retry button is safe — there
 *  is no duplicate-charge risk.
 */
function ErrorModal({ show, error, onRetry, onClose, lang = "th" }) {
  const messages = {
    CARD_DECLINED: {
      th: "บัตรของคุณถูกปฏิเสธ กรุณาตรวจสอบข้อมูลหรือใช้บัตรใบอื่น",
      en: "Your card was declined. Please check the details or try a different card.",
    },
    INSUFFICIENT_FUNDS: {
      th: "ยอดเงินในบัตรไม่เพียงพอ",
      en: "Insufficient funds on your card.",
    },
    EXPIRED_CARD: {
      th: "บัตรหมดอายุแล้ว",
      en: "Card has expired.",
    },
    INVALID_SECURITY_CODE: {
      th: "รหัส CVV ไม่ถูกต้อง",
      en: "Invalid security code.",
    },
    EMAIL_ALREADY_EXISTS: {
      th: "อีเมลนี้ถูกใช้สมัครแล้ว กรุณาใช้อีเมลอื่นหรือเข้าสู่ระบบ",
      en: "This email is already registered. Please use a different email or log in.",
    },
    NETWORK_ERROR: {
      th: "เชื่อมต่อเครือข่ายไม่ได้ กรุณาลองใหม่",
      en: "Can't reach the server. Please try again.",
    },
    UNKNOWN: {
      th: "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง",
      en: "Something went wrong. Please try again.",
    },
  };

  const code = error?.code || "UNKNOWN";
  const friendlyMessage =
    messages[code]?.[lang] || error?.message || messages.UNKNOWN[lang];

  return (
    <StyledModal show={show} onHide={onClose} centered>
      <Body>
        <ErrorCircle>
          <Bang aria-hidden>!</Bang>
        </ErrorCircle>

        <Heading>
          {lang === "en" ? "Payment failed" : "การชำระเงินไม่สำเร็จ"}
        </Heading>

        <Subtitle>{friendlyMessage}</Subtitle>

        <Reassurance>
          {lang === "en"
            ? "No charge has been made to your card."
            : "ยังไม่มีการตัดเงินจากบัตรของคุณ"}
        </Reassurance>

        <ButtonRow>
          <SecondaryButton type="button" onClick={onClose}>
            {lang === "en" ? "Close" : "ปิด"}
          </SecondaryButton>

          {onRetry && (
            <PrimaryButton type="button" onClick={onRetry}>
              {lang === "en" ? "Try again" : "ลองอีกครั้ง"}
            </PrimaryButton>
          )}
        </ButtonRow>
      </Body>
    </StyledModal>
  );
}

export default ErrorModal;

// ─── Animations ──────────────────────────────

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-4px); }
  40%, 80% { transform: translateX(4px); }
`;

// ─── Styled ──────────────────────────────────

const StyledModal = styled(Modal)`
  && .modal-dialog {
    max-width: 440px;
  }

  && .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    background: #ffffff;
  }
`;

const Body = styled.div`
  padding: 32px 28px;
  text-align: center;
`;

const ErrorCircle = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fca5a5, #ef4444);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.3);
  animation: ${shake} 0.5s ease-in-out;
`;

const Bang = styled.span`
  color: #ffffff;
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
`;

const Heading = styled.h5`
  && {
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: 700;
    color: #111827;
  }
`;

const Subtitle = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
`;

const Reassurance = styled.div`
  margin-bottom: 24px;
  padding: 8px 12px;
  background: #f0fdf4;
  border-radius: 6px;
  font-size: 12px;
  color: #15803d;
  font-weight: 500;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const buttonBase = `
  flex: 1;
  height: 44px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
`;

const SecondaryButton = styled.button`
  && {
    ${buttonBase}
    background: #ffffff;
    color: #6b7280;
    border: 1px solid #d1d5db;

    &:hover {
      border-color: #9ca3af;
      color: #374151;
      background: #f9fafb;
    }
  }
`;

const PrimaryButton = styled.button`
  && {
    ${buttonBase}
    background: #6366f1;
    color: #ffffff;
    border: none;

    &:hover {
      background: #4f46e5;
    }
    &:active {
      transform: translateY(1px);
    }
  }
`;